/**
 * WorkflowConditionSummary - Leitura visual das regras de filtro do Workflow 2.0
 *
 * Mostra as regras já configuradas em formato de fichas legíveis
 * ("Origem do lead É UM DE Feira" · "E" · "Produtos CONTÉM ALGUM DE Rastreador"),
 * usado em dois lugares:
 *   - no sheet do inspetor, como resumo clicável (a edição abre o modal);
 *   - dentro do próprio bloco no mapa (versão compacta `compacto`).
 *
 * @version 1.0.0
 * RELEASE: 2026-09-01 v1.0.0 - Criação inicial (filtros robustos do gatilho)
 */

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import { normalizarCondicao, type RegraCondicao } from './WorkflowConditionBuilder';
import { acharCampo, metaDoOperador, rotuloDoCampo, rotuloDoOperador } from './camposDoNegocio';
import { useWorkflowFilterSources } from '@/hooks/useWorkflowFilterSources';

/** Texto legível de uma regra, sem depender de hooks (usado no mapa) */
export function descreverRegra(
  regra: RegraCondicao,
  rotuloDoValor?: (fonte: any, valor: string) => string,
): string {
  const definicao = acharCampo(regra.field);
  const meta = metaDoOperador(regra.field, regra.operator);
  const campo = rotuloDoCampo(regra.field);
  const operador = rotuloDoOperador(regra.field, regra.operator);

  if (meta?.semValor) return `${campo} ${operador}`;

  /** Traduz um valor bruto para o rótulo do catálogo (quando existir) */
  const traduzir = (valor: unknown): string => {
    const texto = String(valor ?? '');
    const fixo = definicao?.opcoes?.find((o) => o.value === texto)?.label;
    if (fixo) return fixo;
    if (rotuloDoValor && definicao?.fonte) return rotuloDoValor(definicao.fonte, texto);
    return texto.length === 36 ? texto.slice(0, 8).toUpperCase() : texto;
  };

  if (meta?.intervalo) return `${campo} ${operador} ${traduzir(regra.value)} e ${traduzir(regra.value2)}`;

  const valores = Array.isArray(regra.value) ? regra.value : [regra.value];
  const textoValores = valores
    .filter((v) => v !== undefined && v !== null && String(v) !== '')
    .map(traduzir)
    .join(', ');

  return `${campo} ${operador} ${textoValores || '—'}`;
}

/** Gera as linhas de texto do resumo (usado pelo mapa/canvas) */
export function linhasDoResumo(condicoes: unknown, maximo = 3): string[] {
  const condicao = normalizarCondicao(condicoes);
  const linhas: string[] = [];

  condicao.groups.forEach((grupo, ig) => {
    grupo.rules.forEach((regra, ir) => {
      const conector = ir > 0 ? (grupo.op === 'and' ? 'E ' : 'OU ') : ig > 0 ? (condicao.op === 'and' ? 'E ' : 'OU ') : '';
      linhas.push(conector + descreverRegra(regra));
    });
  });

  if (linhas.length <= maximo) return linhas;
  return [...linhas.slice(0, maximo), `+${linhas.length - maximo} regra(s)`];
}

interface Props {
  condicoes: unknown;
  /** Versão enxuta (dentro do bloco no mapa) */
  compacto?: boolean;
  className?: string;
  /** Texto exibido quando não há regra */
  vazio?: string;
}

export function WorkflowConditionSummary({
  condicoes,
  compacto = false,
  className,
  vazio = 'Sem filtro — vale para todos os casos.',
}: Props) {
  const fontes = useWorkflowFilterSources();
  const condicao = normalizarCondicao(condicoes);
  const total = condicao.groups.reduce((t, g) => t + g.rules.length, 0);

  if (total === 0) {
    return <p className={cn('text-xs text-muted-foreground', className)}>{vazio}</p>;
  }

  return (
    <div className={cn('space-y-1.5', className)}>
      {condicao.groups.map((grupo, ig) => (
        <div key={ig} className="space-y-1">
          {ig > 0 && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">
              {condicao.op === 'and' ? 'E' : 'OU'}
            </span>
          )}
          <div className={cn('flex flex-wrap items-center gap-1', compacto ? '' : 'rounded-lg border bg-muted/20 p-1.5')}>
            {grupo.rules.map((regra, ir) => (
              <span key={ir} className="flex items-center gap-1">
                {ir > 0 && (
                  <span className="text-[10px] font-semibold uppercase text-muted-foreground">
                    {grupo.op === 'and' ? 'E' : 'OU'}
                  </span>
                )}
                <Badge
                  variant="secondary"
                  className={cn('max-w-full truncate font-normal', compacto ? 'px-1.5 py-0 text-[10px]' : 'text-[11px]')}
                >
                  {descreverRegra(regra, fontes.rotuloDoValor)}
                </Badge>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
